import { Grid } from '@mui/material';
import Textbox from 'component/commonComponents/Textbox/Textbox';
import SelectBox from 'component/commonComponents/selectBox/select';
import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Button } from '../ButtonComponents';
import Dialog from '../Dialog/Dialog';
import Typography from '../Typography/Typography';
import { Address, AddressConfig, Config } from './types';

interface AddressDialogProps {
  open: boolean;
  addressConfig?: AddressConfig;
  initAddress?: Address;
  onClose?: () => void;
  onSubmit?: (address: Address) => void;
  locale?: Record<string, any>;
}

const AddressDialog = (props: AddressDialogProps) => {
  const { open, addressConfig, initAddress, onClose = () => {}, onSubmit, locale, ...rest } = props;

  const theme = useTheme();

  const [address, setAddress] = useState(initAddress || {});
  const [validity, setValidity] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open || initAddress) {
      setAddress(initAddress || {});
      setValidity({});
    }
  }, [open, initAddress]);

  useEffect(() => {
    if (!addressConfig) return;

    setAddress((prevState) => ({
      ...prevState,
      country: addressConfig.country.id,
    }));

    const newValidity = {};
    for (const { field, validation } of addressConfig.config) {
      if (validation?.required) {
        newValidity[field] = true;
      }
    }
    setValidity(newValidity);
  }, [addressConfig]);

  const validateAddress = () => {
    const newValidity = {};
    for (const field in validity) {
      newValidity[field] = !!address[field];
    }
    setValidity(newValidity);

    return Object.values(newValidity).every(Boolean);
  };

  const handleChange = (field: string, value: string) => {
    setAddress((prevState) => ({ ...prevState, [field]: value }));
    if (field in validity) {
      setValidity((prevState) => ({ ...prevState, [field]: true }));
    }
  };

  const handleSubmit = () => {
    const isValid = validateAddress();
    if (isValid) {
      onSubmit?.(address);
      onClose();
    }
  };

  const renderAddressField = (config: Config) => {
    const { type, field, display, options = [], validation, maxlength, idfield = "" } = config;

    switch (type) {
      case 'text':
      case 'textarea': {
        return (
          <Textbox
            type="text"
            label={display?.label}
            id={idfield}
            boldLabel
            labelSize="small"
            placeholder={display?.placeholder}
            name={field}
            value={address[field] || ''}
            error={validity[field] === false}
            onChange={(e) => handleChange(field, e.target.value)}
            fullWidth
            required={validation?.required}
            multiline={type === 'textarea'}
            rows={2}
            inputProps={{ maxLength: maxlength }}
            textboxToolTip={display?.label}
          />
        );
      }
      case 'dropdown': {
        const value = options.find((option) => option.id === address[field]);
        return (
          <SelectBox
            label={display?.label}
            id={idfield}
            boldLabel
            name={field}
            handleChange={(e: unknown, val: typeof value) => handleChange(field, val!.id)}
            options={options}
            value={value || {}}
            error={validity[field] === false}
            valueKey="id"
            disableClearable
            placeholder={display?.placeholder}
            required={validation?.required}
            textboxToolTip={display?.label}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const content = (
    <Grid container spacing={`${theme.uptimeSpace[10]}px`}>
      <Grid item xs={12}>
        <SelectBox
          label="Country"
          boldLabel
          name="country"
          value={addressConfig?.country || {}}
          error={false}
          placeholder=""
          disableClearable
          required={false}
          disabled
          textboxToolTip='Country'
        />
      </Grid>
      {addressConfig?.config.map((item) => (
        <Grid item xs={12}>
          {renderAddressField(item)}
        </Grid>
      ))}
    </Grid>
  );

  const actions = (
    <>
      <Button onClick={onClose} variant="text" id="btn-addressdialog-cancel">
        {locale?.shoppingCart?.cancel}
      </Button>
      <Button onClick={handleSubmit} variant="text" id="btn-addressdialog-submit">
        {locale?.shoppingCart?.saveAddress}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      title={<Typography variant="h5">{locale?.shoppingCart?.editShippingAddress}</Typography>}
      content={content}
      onClose={onClose}
      actions={actions}
      maxWidth="sm"
      {...rest}
    />
  );
};

export default AddressDialog;
